import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
// --- Data: Names, Cities, Fallback Catalog ---
const firstNames = ['James', 'Emily', 'Michael', 'Sarah', 'David', 'Jessica', 'Chris', 'Ashley', 'Daniel', 'Amanda', 'Matthew', 'Stephanie', 'Andrew', 'Nicole', 'Joshua', 'Lauren', 'Brandon', 'Megan', 'Ryan', 'Rachel', 'Tyler', 'Brittany', 'Kevin', 'Samantha', 'Nathan', 'Kayla', 'Justin', 'Hannah', 'Brian', 'Olivia'];
const cities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'Austin', 'Jacksonville', 'San Jose', 'Fort Worth', 'Columbus', 'Charlotte', 'Indianapolis', 'San Francisco', 'Seattle', 'Denver', 'Nashville', 'Portland', 'Miami', 'Atlanta', 'Boston', 'Tampa', 'Minneapolis', 'Raleigh', 'Salt Lake City', 'Scottsdale', 'Brooklyn'];
const timestamps = ['Just now', '1 minute ago', '2 minutes ago', '3 minutes ago', '5 minutes ago', '8 minutes ago', '12 minutes ago'];
interface ProductVariant {
  title: string;
  editionLabel: string;
  priceNumber: number;
  thumbnailUrl: string;
  weight: number;
}
const fallbackCatalog: ProductVariant[] = [{
  title: 'LumiMate Duo (2 Units)',
  editionLabel: 'Bestseller - Office Setup Pack',
  priceNumber: 39.97,
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/mgjzJDViwYTN4NGXT5NUX4/81EQM2B1Zy6L.jpg',
  weight: 60
}, {
  title: 'LumiMate Single',
  editionLabel: 'Starter Edition',
  priceNumber: 24.99,
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/gxK9FhSEDowV626yk5TdsP/61J5pIRK-7L.jpg',
  weight: 25
}, {
  title: 'LumiMate Family Bundle (3 Units)',
  editionLabel: 'Ultimate Gift Pack',
  priceNumber: 54.95,
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/t3acGdCTtAQvVU8pYpWvER/creative_43fea84(768x1344)_(1).jpg',
  weight: 15
}];
// --- Helpers ---
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeighted(variants: ProductVariant[]): ProductVariant {
  const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
  let rand = Math.random() * totalWeight;
  for (const variant of variants) {
    rand -= variant.weight;
    if (rand <= 0) return variant;
  }
  return variants[0];
}
function scanDomForProducts(): ProductVariant[] {
  const found: ProductVariant[] = [];
  try {
    // Look for pricing cards with bundle info
    const priceRegex = /\$([0-9]+(?:\.[0-9]{2})?)/g;
    const bundleRegex = /buy\s*(\d+)/i;
    // Scan all text nodes that contain price patterns near bundle labels
    const elements = document.querySelectorAll('[class*="pricing"], [class*="bundle"], h3, span');
    const priceMap = new Map<number, {
      qty: number;
      price: number;
    }>();
    elements.forEach(el => {
      const text = el.textContent || '';
      const bundleMatch = text.match(bundleRegex);
      const priceMatch = text.match(priceRegex);
      if (bundleMatch && priceMatch) {
        const qty = parseInt(bundleMatch[1], 10);
        const price = parseFloat(priceMatch[0].replace('$', ''));
        if (!isNaN(qty) && !isNaN(price)) {
          priceMap.set(qty, {
            qty,
            price
          });
        }
      }
    });
    // Also try scanning for price values near "Buy" text
    document.querySelectorAll('h3').forEach(h3 => {
      const text = h3.textContent || '';
      const match = text.match(bundleRegex);
      if (match) {
        const qty = parseInt(match[1], 10);
        const parent = h3.closest('[class*="rounded"]');
        if (parent) {
          const priceText = parent.textContent || '';
          const prices = [...priceText.matchAll(priceRegex)];
          if (prices.length > 0) {
            const lastPrice = parseFloat(prices[prices.length - 1][0].replace('$', ''));
            if (!isNaN(lastPrice)) {
              priceMap.set(qty, {
                qty,
                price: lastPrice
              });
            }
          }
        }
      }
    });
    // Convert found prices to product variants
    const images = ['https://cdn.magicpatterns.com/uploads/mgjzJDViwYTN4NGXT5NUX4/81EQM2B1Zy6L.jpg', 'https://cdn.magicpatterns.com/uploads/gxK9FhSEDowV626yk5TdsP/61J5pIRK-7L.jpg', 'https://cdn.magicpatterns.com/uploads/t3acGdCTtAQvVU8pYpWvER/creative_43fea84(768x1344)_(1).jpg'];
    priceMap.forEach(({
      qty,
      price
    }) => {
      let label = 'Starter Edition';
      let title = 'LumiMate Single';
      let weight = 25;
      if (qty === 2) {
        label = 'Bestseller - Office Setup Pack';
        title = 'LumiMate Duo (2 Units)';
        weight = 60;
      } else if (qty >= 4 && qty < 6) {
        label = 'Power User Bundle';
        title = `LumiMate ${qty}-Pack`;
        weight = 20;
      } else if (qty >= 6) {
        label = 'Ultimate Value Bundle';
        title = `LumiMate ${qty}-Pack`;
        weight = 15;
      }
      found.push({
        title,
        editionLabel: label,
        priceNumber: price,
        thumbnailUrl: pickRandom(images),
        weight
      });
    });
  } catch {
    // Silent fail — fallback catalog will be used
  }
  return found;
}
// --- Notification type ---
interface Notification {
  id: string;
  name: string;
  city: string;
  variant: ProductVariant;
  timestamp: string;
}
// --- Component ---
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const shownIds = useRef(new Set<string>());
  const catalogRef = useRef<ProductVariant[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Initialize catalog on mount
  useEffect(() => {
    const domProducts = scanDomForProducts();
    catalogRef.current = domProducts.length > 0 ? domProducts : fallbackCatalog;
    // Boost Duo Pack weight if found
    catalogRef.current = catalogRef.current.map(v => v.title.toLowerCase().includes('duo') ? {
      ...v,
      weight: Math.max(v.weight, 60)
    } : v);
  }, []);
  const generateNotification = useCallback((): Notification | null => {
    const catalog = catalogRef.current;
    if (catalog.length === 0) return null;
    // Try up to 10 times to find a unique combo
    for (let i = 0; i < 10; i++) {
      const name = pickRandom(firstNames);
      const city = pickRandom(cities);
      const variant = pickWeighted(catalog);
      const id = `${name}-${city}-${variant.editionLabel}`;
      if (!shownIds.current.has(id)) {
        shownIds.current.add(id);
        return {
          id,
          name,
          city,
          variant,
          timestamp: pickRandom(timestamps)
        };
      }
    }
    // If all combos exhausted, reset and try once more
    shownIds.current.clear();
    const name = pickRandom(firstNames);
    const city = pickRandom(cities);
    const variant = pickWeighted(catalog);
    return {
      id: `${name}-${city}-${variant.editionLabel}`,
      name,
      city,
      variant,
      timestamp: pickRandom(timestamps)
    };
  }, []);
  const showNext = useCallback(() => {
    const next = generateNotification();
    if (next) {
      setNotification(next);
      setVisible(true);
      // Auto-hide after 6 seconds
      setTimeout(() => {
        setVisible(false);
      }, 6000);
    }
  }, [generateNotification]);
  const scheduleNext = useCallback(() => {
    const delay = 25000 + Math.random() * 20000; // 25-45 seconds (variable)
    timerRef.current = setTimeout(() => {
      showNext();
      scheduleNext();
    }, delay);
  }, [showNext]);
  useEffect(() => {
    // First toast after 8 seconds
    const initialTimer = setTimeout(() => {
      showNext();
      scheduleNext();
    }, 8000);
    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNext, scheduleNext]);
  const dismiss = () => setVisible(false);
  return <AnimatePresence data-id="element-111">
      {visible && notification && <>
          {/* Desktop: bottom-left */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        x: -10
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="hidden sm:flex fixed bottom-6 left-6 z-50 max-w-sm bg-[#1A1A22] border border-white/10 rounded-xl shadow-2xl shadow-black/40 p-4 items-start gap-3" data-id="element-112">
            <div className="flex-1 min-w-0" data-id="element-113">
              <p className="text-white text-sm font-semibold leading-snug" data-id="element-114">
                {notification.name} from {notification.city}
              </p>
              <p className="text-brand-muted text-xs mt-0.5" data-id="element-115">
                just purchased the{' '}
                <span className="text-brand-orange font-medium" data-id="element-116">
                  {notification.variant.editionLabel}
                </span>
              </p>
              <div className="flex items-center gap-2 mt-2" data-id="element-117">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-green-400" data-id="element-118" />
                <span className="text-green-400 text-xs font-medium" data-id="element-119">
                  Verified Purchase
                </span>
                <span className="text-white/20 text-xs" data-id="element-120">•</span>
                <span className="text-brand-muted text-xs" data-id="element-121">
                  {notification.timestamp}
                </span>
              </div>
            </div>
            <button onClick={dismiss} className="text-white/30 hover:text-white/60 transition-colors shrink-0 mt-0.5" data-id="element-122">
              <XIcon className="w-4 h-4" data-id="element-123" />
            </button>
          </motion.div>

          {/* Mobile: top-center */}
          <motion.div initial={{
        opacity: 0,
        y: -30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -30
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="flex sm:hidden fixed top-4 left-4 right-4 z-50 bg-[#1A1A22] border border-white/10 rounded-xl shadow-2xl shadow-black/40 p-3 items-start gap-3" data-id="element-124">
            <div className="flex-1 min-w-0" data-id="element-125">
              <p className="text-white text-sm font-semibold leading-snug" data-id="element-126">
                {notification.name} from {notification.city}
              </p>
              <p className="text-brand-muted text-xs mt-0.5" data-id="element-127">
                just purchased the{' '}
                <span className="text-brand-orange font-medium" data-id="element-128">
                  {notification.variant.editionLabel}
                </span>
              </p>
              <div className="flex items-center gap-2 mt-1.5" data-id="element-129">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-green-400" data-id="element-130" />
                <span className="text-green-400 text-xs font-medium" data-id="element-131">
                  Verified
                </span>
                <span className="text-white/20 text-xs" data-id="element-132">•</span>
                <span className="text-brand-muted text-xs" data-id="element-133">
                  {notification.timestamp}
                </span>
              </div>
            </div>
            <button onClick={dismiss} className="text-white/30 hover:text-white/60 transition-colors shrink-0" data-id="element-134">
              <XIcon className="w-4 h-4" data-id="element-135" />
            </button>
          </motion.div>
        </>}
    </AnimatePresence>;
}